import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useErrorLogs,
  useResolveError,
  useUnresolveError,
  type ErrorLog,
  type ErrorSeverity,
  type ErrorSource,
} from "@/hooks/useErrorLogs";
import { format } from "date-fns";
import {
  AlertTriangle,
  RefreshCw,
  Filter,
  Search,
  CheckCircle2,
  XCircle,
  Info,
  AlertCircle,
} from "lucide-react";

// ============================================================================
// Constants
// ============================================================================

const SEVERITY_OPTIONS: { value: ErrorSeverity | "all"; label: string }[] = [
  { value: "all", label: "All Severities" },
  { value: "critical", label: "Critical" },
  { value: "error", label: "Error" },
  { value: "warning", label: "Warning" },
  { value: "info", label: "Info" },
];

const SOURCE_OPTIONS: { value: ErrorSource | "all"; label: string }[] = [
  { value: "all", label: "All Sources" },
  { value: "document-parser", label: "Document Parser" },
  { value: "email-processor", label: "Email Processor" },
  { value: "dropbox-api", label: "Dropbox API" },
  { value: "vintrace-api", label: "Vintrace API" },
  { value: "database", label: "Database" },
  { value: "edge-function", label: "Edge Function" },
  { value: "frontend", label: "Frontend" },
];

const RESOLVED_OPTIONS = [
  { value: "all", label: "All" },
  { value: "unresolved", label: "Unresolved Only" },
  { value: "resolved", label: "Resolved Only" },
];

const SEVERITY_COLORS: Record<ErrorSeverity, string> = {
  info: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  warning: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
  error: "bg-red-500/10 text-red-600 border-red-500/20",
  critical: "bg-purple-500/10 text-purple-600 border-purple-500/20",
};

const SEVERITY_ICONS: Record<ErrorSeverity, React.ReactNode> = {
  info: <Info className="h-3 w-3" />,
  warning: <AlertTriangle className="h-3 w-3" />,
  error: <XCircle className="h-3 w-3" />,
  critical: <AlertCircle className="h-3 w-3" />,
};

const SOURCE_COLORS: Record<ErrorSource, string> = {
  "document-parser": "bg-orange-500/10 text-orange-600 border-orange-500/20",
  "email-processor": "bg-teal-500/10 text-teal-600 border-teal-500/20",
  "dropbox-api": "bg-blue-500/10 text-blue-600 border-blue-500/20",
  "vintrace-api": "bg-green-500/10 text-green-600 border-green-500/20",
  database: "bg-gray-500/10 text-gray-600 border-gray-500/20",
  "edge-function": "bg-indigo-500/10 text-indigo-600 border-indigo-500/20",
  frontend: "bg-pink-500/10 text-pink-600 border-pink-500/20",
};

// ============================================================================
// Component
// ============================================================================

export function ErrorLogsTab() {
  // Filter state
  const [severity, setSeverity] = useState<ErrorSeverity | "all">("all");
  const [source, setSource] = useState<ErrorSource | "all">("all");
  const [resolvedFilter, setResolvedFilter] = useState("unresolved");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Dialog state
  const [selectedError, setSelectedError] = useState<ErrorLog | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState("");

  // Data fetching
  const { data: logs, isLoading, refetch } = useErrorLogs({
    severity: severity === "all" ? undefined : severity,
    source: source === "all" ? undefined : source,
    resolved:
      resolvedFilter === "all"
        ? undefined
        : resolvedFilter === "resolved"
        ? true
        : false,
    startDate: startDate ? new Date(startDate) : undefined,
    endDate: endDate ? new Date(endDate) : undefined,
    searchQuery: searchQuery || undefined,
  });

  // Mutations
  const resolveError = useResolveError();
  const unresolveError = useUnresolveError();

  // Handlers
  const handleViewDetails = (log: ErrorLog) => {
    setSelectedError(log);
    setResolutionNotes(log.resolution_notes || "");
    setDetailsOpen(true);
  };

  const handleResolve = async () => {
    if (!selectedError) return;
    await resolveError.mutateAsync({
      errorId: selectedError.id,
      resolutionNotes: resolutionNotes || undefined,
    });
    setDetailsOpen(false);
  };

  const handleUnresolve = async () => {
    if (!selectedError) return;
    await unresolveError.mutateAsync(selectedError.id);
    setDetailsOpen(false);
  };

  const clearFilters = () => {
    setSeverity("all");
    setSource("all");
    setResolvedFilter("unresolved");
    setStartDate("");
    setEndDate("");
    setSearchQuery("");
  };

  const formatSourceLabel = (src: ErrorSource) => {
    const option = SOURCE_OPTIONS.find((o) => o.value === src);
    return option?.label || src;
  };

  const renderContext = (context: Record<string, any>) => {
    if (!context || Object.keys(context).length === 0) {
      return <p className="text-sm text-muted-foreground">No additional context</p>;
    }

    return (
      <pre className="text-xs p-3 bg-muted rounded-lg overflow-x-auto">
        {JSON.stringify(context, null, 2)}
      </pre>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Error Logs
          </CardTitle>
          <CardDescription>
            View system errors from document parsing, email processing, and other sources
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Filters */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-medium">Filters</h3>
            </div>

            <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
              <div className="space-y-2">
                <Label>Severity</Label>
                <Select
                  value={severity}
                  onValueChange={(v) => setSeverity(v as ErrorSeverity | "all")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Source</Label>
                <Select
                  value={source}
                  onValueChange={(v) => setSource(v as ErrorSource | "all")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={resolvedFilter} onValueChange={setResolvedFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RESOLVED_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-4 items-end">
              <div className="flex-1 max-w-sm space-y-2">
                <Label>Search Message</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search error messages..."
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <Button variant="outline" size="sm" onClick={clearFilters}>
                Clear Filters
              </Button>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          <Separator />

          {/* Error Log List */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">
                Errors ({logs?.length || 0})
              </h3>
              {logs && logs.some((l) => !l.resolved) && (
                <Badge variant="destructive">
                  {logs.filter((l) => !l.resolved).length} unresolved
                </Badge>
              )}
            </div>

            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading error logs...
              </div>
            ) : logs && logs.length > 0 ? (
              <ScrollArea className="h-[500px] rounded-md border">
                <div className="p-4 space-y-2">
                  {logs.map((log) => (
                    <Card
                      key={log.id}
                      className={`cursor-pointer hover:bg-accent transition-colors ${
                        log.resolved ? "opacity-60" : ""
                      }`}
                      onClick={() => handleViewDetails(log)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-2 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge
                                variant="outline"
                                className={`flex items-center gap-1 ${
                                  SEVERITY_COLORS[log.severity]
                                }`}
                              >
                                {SEVERITY_ICONS[log.severity]}
                                {log.severity.toUpperCase()}
                              </Badge>
                              <Badge
                                variant="outline"
                                className={SOURCE_COLORS[log.source]}
                              >
                                {formatSourceLabel(log.source)}
                              </Badge>
                              {log.error_code && (
                                <Badge variant="secondary">{log.error_code}</Badge>
                              )}
                              {log.resolved && (
                                <Badge
                                  variant="outline"
                                  className="bg-green-500/10 text-green-600 border-green-500/20"
                                >
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Resolved
                                </Badge>
                              )}
                            </div>

                            <p className="text-sm font-medium truncate">
                              {log.message}
                            </p>

                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span>{format(new Date(log.occurred_at), "PPpp")}</span>
                              {log.entity_type && (
                                <span>
                                  {log.entity_type}: {log.entity_id?.slice(0, 8)}...
                                </span>
                              )}
                              {log.user?.display_name && (
                                <span>User: {log.user.display_name}</span>
                              )}
                            </div>
                          </div>

                          <Button variant="ghost" size="sm">
                            <AlertTriangle className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-4 opacity-50 text-green-500" />
                <p>No errors found</p>
                <p className="text-sm">Try adjusting your filters</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Error Details
            </DialogTitle>
            <DialogDescription>
              Full details of this error log entry
            </DialogDescription>
          </DialogHeader>

          {selectedError && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground mb-1">Severity</p>
                  <Badge
                    variant="outline"
                    className={`flex items-center gap-1 w-fit ${
                      SEVERITY_COLORS[selectedError.severity]
                    }`}
                  >
                    {SEVERITY_ICONS[selectedError.severity]}
                    {selectedError.severity.toUpperCase()}
                  </Badge>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Source</p>
                  <Badge
                    variant="outline"
                    className={SOURCE_COLORS[selectedError.source]}
                  >
                    {formatSourceLabel(selectedError.source)}
                  </Badge>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Error Code</p>
                  <p className="font-medium">
                    {selectedError.error_code || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Timestamp</p>
                  <p className="font-medium">
                    {format(new Date(selectedError.occurred_at), "PPpp")}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Entity</p>
                  <p className="font-mono text-xs">
                    {selectedError.entity_type
                      ? `${selectedError.entity_type}: ${selectedError.entity_id}`
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Request ID</p>
                  <p className="font-mono text-xs">
                    {selectedError.request_id || "N/A"}
                  </p>
                </div>
              </div>

              <Separator />

              {/* Error Message */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Error Message</h4>
                <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
                  <p className="text-sm">{selectedError.message}</p>
                </div>
              </div>

              {/* Stack Trace */}
              {selectedError.stack_trace && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Stack Trace</h4>
                  <ScrollArea className="h-48 rounded-lg border">
                    <pre className="text-xs p-3 whitespace-pre-wrap">
                      {selectedError.stack_trace}
                    </pre>
                  </ScrollArea>
                </div>
              )}

              {/* Context */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Context</h4>
                {renderContext(selectedError.context)}
              </div>

              <Separator />

              {/* Resolution Section */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Resolution</h4>

                {selectedError.resolved ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
                      <div className="flex items-center gap-2 text-green-600 mb-2">
                        <CheckCircle2 className="h-4 w-4" />
                        <span className="font-medium">Resolved</span>
                      </div>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p>
                          By: {selectedError.resolver?.display_name || "Unknown"}
                        </p>
                        <p>
                          At:{" "}
                          {selectedError.resolved_at
                            ? format(new Date(selectedError.resolved_at), "PPpp")
                            : "Unknown"}
                        </p>
                        {selectedError.resolution_notes && (
                          <p className="mt-2">
                            Notes: {selectedError.resolution_notes}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUnresolve}
                      disabled={unresolveError.isPending}
                    >
                      Mark as Unresolved
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="resolution-notes">Resolution Notes (optional)</Label>
                      <Textarea
                        id="resolution-notes"
                        placeholder="Describe how this error was resolved..."
                        value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e.target.value)}
                        rows={3}
                      />
                    </div>
                    <Button
                      onClick={handleResolve}
                      disabled={resolveError.isPending}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Mark as Resolved
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
